<template>
  <section
    class="hero"
    @mousedown="handleDragStart"
    @mousemove="handleDragMove"
    @mouseup="handleDragEnd"
    @mouseleave="handleDragEnd"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <div class="hero-stage">
      <article
        v-for="(slide, index) in slides"
        :key="slide.imageClass + index"
        class="hero-slide"
        :class="[
          slide.imageClass,
          {
            'is-active': index === activeIndex,
            'is-prev': index === previousIndex
          }
        ]"
        :style="slideBackgroundStyle(slide)"
      >
        <div class="hero-overlay"></div>

        <div class="container hero-inner">
          <div class="hero-copy" :class="{ 'is-active': index === activeIndex }">
            <p class="hero-tag">{{ slide.tag }}</p>
            <h1 :class="{ 'is-zh-title': locale === 'zh-CN' }">{{ $pick(slide.title) }}</h1>
            <span>{{ $pick(slide.subtitle) }}</span>
            <div class="hero-actions">
              <router-link class="hero-button" to="/products">{{ $pick(slide.cta) }}</router-link>
              <router-link class="hero-link" to="/about/company">{{ moreText }}</router-link>
            </div>
          </div>

          <div class="hero-visual" :class="[slide.visualClass, { 'is-active': index === activeIndex }]">
            <div class="visual-frame">
              <div class="visual-screen">
                <img :src="slide.visualImageUrl" :alt="$pick(slide.title)" />
              </div>
<!--              <div class="visual-card">-->
<!--                <strong>{{ visualTitle }}</strong>-->
<!--                <p>{{ visualSubtitle }}</p>-->
<!--              </div>-->
            </div>
            <div class="visual-float float-top">{{ floatTop }}</div>
            <div class="visual-float float-bottom">{{ floatBottom }}</div>
          </div>
        </div>
      </article>
    </div>

    <div class="hero-dots">
      <button
        v-for="(item, index) in slides"
        :key="index"
        :class="{ active: activeIndex === index }"
        @click="goTo(index)"
      ></button>
    </div>
  </section>
</template>

<script>
export default {
  name: "HeroCarousel",
  props: {
    slides: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      activeIndex: 0,
      previousIndex: 0,
      timer: null,
      dragStartX: 0,
      dragDeltaX: 0,
      isDragging: false
    };
  },
  computed: {
    moreText() {
      return this.locale === "zh-CN" ? "了解更多" : "Discover More";
    },
    visualTitle() {
      return this.locale === "zh-CN" ? "智能制造基地" : "Smart Manufacturing Base";
    },
    visualSubtitle() {
      return this.locale === "zh-CN"
        ? "电力装备展示站首屏视觉模块"
        : "Hero visual module for a power equipment website";
    },
    floatTop() {
      return this.locale === "zh-CN" ? "输配电装备" : "Power Equipment";
    },
    floatBottom() {
      return this.locale === "zh-CN" ? "新能源解决方案" : "New Energy Solutions";
    }
  },
  mounted() {
    this.startAutoPlay();
  },
  beforeDestroy() {
    window.clearInterval(this.timer);
  },
  methods: {
    startAutoPlay() {
      window.clearInterval(this.timer);
      this.timer = window.setInterval(() => {
        this.nextSlide();
      }, 6000);
    },
    goTo(index) {
      this.previousIndex = this.activeIndex;
      this.activeIndex = index;
      this.startAutoPlay();
    },
    nextSlide() {
      this.goTo((this.activeIndex + 1) % this.slides.length);
    },
    prevSlide() {
      this.goTo((this.activeIndex - 1 + this.slides.length) % this.slides.length);
    },
    slideBackgroundStyle(slide) {
      if (!slide.imageUrl) {
        return {};
      }

      return {
        backgroundImage: `linear-gradient(115deg, rgba(8, 20, 38, 0.12), rgba(8, 20, 38, 0.02)), url('${slide.imageUrl}')`,
        backgroundPosition: "center center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat"
      };
    },
    handleDragStart(event) {
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragDeltaX = 0;
    },
    handleDragMove(event) {
      if (!this.isDragging) {
        return;
      }
      this.dragDeltaX = event.clientX - this.dragStartX;
    },
    handleDragEnd() {
      if (!this.isDragging) {
        return;
      }

      if (this.dragDeltaX < -70) {
        this.nextSlide();
      } else if (this.dragDeltaX > 70) {
        this.prevSlide();
      } else {
        this.startAutoPlay();
      }

      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;
    },
    handleTouchStart(event) {
      if (!event.touches || !event.touches.length) {
        return;
      }
      this.isDragging = true;
      this.dragStartX = event.touches[0].clientX;
      this.dragDeltaX = 0;
    },
    handleTouchMove(event) {
      if (!this.isDragging || !event.touches || !event.touches.length) {
        return;
      }
      this.dragDeltaX = event.touches[0].clientX - this.dragStartX;
    },
    handleTouchEnd() {
      this.handleDragEnd();
    }
  }
};
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: #071a32;
  cursor: grab;
  user-select: none;
}

.hero:active {
  cursor: grabbing;
}

.hero-stage {
  position: relative;
  min-height: 100vh;
}

.hero-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transform: scale(1.04);
  transition: opacity 0.9s ease, transform 1.4s ease;
  pointer-events: none;
}

.hero-slide.is-active {
  opacity: 1;
  transform: scale(1);
  pointer-events: auto;
  z-index: 2;
}

.hero-slide.is-prev {
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(4, 17, 34, 0.82) 0%, rgba(4, 17, 34, 0.56) 34%, rgba(4, 17, 34, 0.22) 62%, rgba(4, 17, 34, 0.04) 100%),
    linear-gradient(180deg, rgba(5, 14, 28, 0.08), rgba(5, 14, 28, 0.34));
}

.hero-image-one {
}

.hero-image-two {
}

.hero-inner {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 0.72fr);
  gap: 28px;
  align-items: center;
  min-height: 100vh;
  padding-top: 148px;
  padding-bottom: 84px;
}

.hero-copy {
  max-width: 760px;
  opacity: 0;
  transform: translate3d(0, 54px, 0);
}

.hero-copy.is-active {
  animation: textReveal 1.1s cubic-bezier(0.2, 0.8, 0.2, 1) 0.18s forwards;
}

.hero-tag {
  margin: 0 0 18px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  letter-spacing: 0.28em;
}

.hero-copy h1 {
  margin: 0 0 18px;
  color: #ffffff;
  font-size: clamp(44px, 5.9vw, 52px);
  line-height: 1.02;
  text-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
}

.hero-copy h1.is-zh-title {
  white-space: nowrap;
}

.hero-copy span {
  display: block;
  max-width: 34em;
  margin-bottom: 34px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 20px;
  line-height: 1.7;
  text-shadow: 0 10px 22px rgba(0, 0, 0, 0.16);
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-button,
.hero-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  min-height: 35px;
  transition: transform 0.28s ease, background-color 0.28s ease, color 0.28s ease, border-color 0.28s ease;
}

.hero-button {
  color: #fff;
  background: linear-gradient(135deg, #1a6fcb, #39a0ff);
  box-shadow: 0 20px 36px rgba(25, 102, 193, 0.28);
}

.hero-button:hover,
.hero-link:hover {
  transform: translateY(-3px);
}

.hero-link {
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.36);
  background: rgba(255, 255, 255, 0.05);
}

.hero-visual {
  position: relative;
  justify-self: end;
  width: min(100%, 520px);
  opacity: 0;
  transform: translate3d(62px, 20px, 0) scale(0.96);
}

.hero-visual.is-active {
  animation: mediaReveal 1.15s cubic-bezier(0.18, 0.8, 0.2, 1) 0.36s forwards;
}

.visual-frame {
  position: relative;
  height: 520px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.04));
  border: 1px solid rgba(255, 255, 255, 0.24);
  overflow: hidden;
  box-shadow: 0 26px 80px rgba(0, 14, 34, 0.42);
  backdrop-filter: blur(16px);
}

.visual-screen {
  position: absolute;
  inset: 16px;
  border-radius: 22px;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.06)),
    rgba(4, 24, 46, 0.28);
}

.visual-screen img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: drop-shadow(0 24px 34px rgba(0, 8, 18, 0.32));
}

.visual-card {
  position: absolute;
  left: 30px;
  bottom: 30px;
  width: 240px;
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(3, 18, 34, 0.62);
  color: #fff;
  backdrop-filter: blur(10px);
}

.visual-card strong {
  display: block;
  margin-bottom: 8px;
  font-size: 18px;
}

.visual-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  opacity: 0.88;
}

.visual-float {
  position: absolute;
  padding: 12px 18px;
  color: #fff;
  font-size: 14px;
  letter-spacing: 0.06em;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(10px);
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.22);
}

.float-top {
  top: 32px;
  right: -12px;
}

.float-bottom {
  left: -20px;
  bottom: 50px;
}

.hero-dots {
  position: absolute;
  left: 50%;
  bottom: 30px;
  z-index: 4;
  display: flex;
  gap: 12px;
  transform: translateX(-50%);
}

.hero-dots button {
  width: 12px;
  height: 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: transform 0.25s ease, background-color 0.25s ease;
}

.hero-dots button.active {
  background: #ffffff;
  transform: scale(1.14);
}

@keyframes textReveal {
  from {
    opacity: 0;
    transform: translate3d(0, 54px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes mediaReveal {
  from {
    opacity: 0;
    transform: translate3d(62px, 20px, 0) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
}

@media (max-width: 980px) {
  .hero-inner {
    grid-template-columns: 1fr;
    gap: 30px;
    padding-top: 144px;
    padding-bottom: 90px;
  }

  .hero-copy span {
    font-size: 18px;
  }

  .hero-visual {
    justify-self: start;
  }
}

@media (min-width: 901px) and (max-width: 1600px) {
  .hero-inner {
    grid-template-columns: 1fr;
  }

  .hero-copy {
    max-width: none;
  }

  .hero-visual {
    display: none;
  }
}

@media (max-width: 760px) {
  .hero {
    min-height: 680px;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .hero-stage,
  .hero-slide {
    min-height: 680px;
  }

  .hero-inner {
    min-height: 680px;
  }

  .hero-inner {
    gap: 22px;
    align-content: center;
    padding: 96px 18px 66px;
    width: 100%;
    max-width: 100%;
  }

  .hero-copy {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .hero-tag {
    margin-bottom: 12px;
    font-size: 12px;
    letter-spacing: 0.18em;
  }

  .hero-copy h1 {
    font-size: 34px;
    line-height: 1.14;
    white-space: normal;
    overflow-wrap: break-word;
  }

  .hero-copy h1.is-zh-title {
    white-space: normal;
  }

  .hero-copy span {
    max-width: 100%;
    font-size: 16px;
    line-height: 1.65;
    margin-bottom: 24px;
  }

  .hero-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    max-width: 100%;
  }

  .hero-button,
  .hero-link {
    min-width: 0;
    min-height: 42px;
    padding: 0 12px;
    font-size: 14px;
    width: 100%;
  }

  .hero-visual {
    width: 100%;
    max-width: 340px;
    justify-self: center;
    overflow: hidden;
  }

  .visual-frame {
    height: 260px;
    border-radius: 20px;
  }

  .visual-card {
    left: 18px;
    right: 18px;
    bottom: 18px;
    width: auto;
    padding: 14px 16px;
    border-radius: 14px;
  }

  .visual-card strong {
    font-size: 15px;
  }

  .visual-card p {
    font-size: 12px;
  }

  .float-top {
    right: 12px;
    top: 18px;
  }

  .float-bottom {
    left: 12px;
    bottom: 66px;
  }

  .visual-float {
    padding: 9px 12px;
    font-size: 12px;
  }

  .hero-dots {
    bottom: 20px;
  }
}

@media (max-width: 420px) {
  .hero,
  .hero-stage,
  .hero-slide,
  .hero-inner {
    min-height: 640px;
  }

  .hero-inner {
    padding-left: 16px;
    padding-right: 16px;
  }

  .hero-copy h1 {
    font-size: 30px;
  }

  .hero-actions {
    grid-template-columns: 1fr;
  }

  .visual-frame {
    height: 220px;
  }
}
</style>
